/**
 * Mirrors backend PHP ms365bin(): decodes MS365 "Includes:<bitmask>" into internal shortcut keys.
 * Order and match condition must stay aligned with the server implementation.
 */
const MS365_SHORTCUTS: ReadonlyArray<{ key: string; val: number }> = [
  { key: 'G2-MS365-OutlookArchive', val: 128 },
  { key: 'G3-MS365-TeamsChannel', val: 64 },
  { key: 'G3-MS365-TeamsGroupSite', val: 32 },
  { key: 'G3-MS365-TeamsGroupMail', val: 16 },
  { key: 'G2-MS365-TeamsChat', val: 8 },
  { key: 'G2-MS365-PersonalSite', val: 4 },
  { key: 'G2-MS365-OneDrive', val: 2 },
  { key: 'G2-MS365-Outlook', val: 1 },
];

const MS365_DISPLAY_LABELS: Readonly<Record<string, string>> = {
  'G2-MS365-OutlookArchive': 'Outlook archive',
  'G3-MS365-TeamsChannel': 'Teams channels',
  'G3-MS365-TeamsGroupSite': 'Teams group site',
  'G3-MS365-TeamsGroupMail': 'Teams group mail',
  'G2-MS365-TeamsChat': 'Teams chat',
  'G2-MS365-PersonalSite': 'Personal site',
  'G2-MS365-OneDrive': 'OneDrive',
  'G2-MS365-Outlook': 'Outlook',
};

const INCLUDES_PATTERN = /^Includes:\s*(\d+)\s*$/i;

function decodeMs365Bitmask(bin: number): { keys: string[]; remainder: number } {
  const keys: string[] = [];
  let remaining = bin;

  MS365_SHORTCUTS.forEach(({ key, val }) => {
    const left = remaining - val;
    const absVal = Math.abs(val);
    if (left >= 0 && left < absVal) {
      keys.push(key);
      remaining = left;
    }
  });

  return { keys, remainder: remaining };
}

/** Returns internal shortcut keys only (same as PHP `ms365bin(..., false)`). */
export function ms365bin(bin: number): string[] {
  return decodeMs365Bitmask(bin).keys;
}

export function formatMs365IncludeLabels(keys: string[]): string[] {
  return keys.map((k) => MS365_DISPLAY_LABELS[k] ?? k);
}

/**
 * Expands each `Includes:<n>` line into human-readable MS365 service rows; other lines pass through.
 */
export function expandSelectedSourceListItems(items: string[]): string[] {
  return items.flatMap((item) => {
    const trimmed = item.trim();
    const m = trimmed.match(INCLUDES_PATTERN);
    if (!m) {
      return [item];
    }
    const n = parseInt(m[1], 10);
    if (Number.isNaN(n)) {
      return [item];
    }
    const { keys, remainder } = decodeMs365Bitmask(n);
    const labels = formatMs365IncludeLabels(keys);
    if (remainder !== 0) {
      labels.push(`Includes: ${remainder} (unrecognized)`);
    }
    return labels;
  });
}
